/**
 * Klagenfurt P0 2026-08 (User brief D) — DealComparables Chat Widget
 *
 * Server-built widget the DealAdvisor attaches to a chat turn when
 * the user asks for the comparables of the currently opened deal.
 * Renders inline in the advisor conversation (no canvas needed —
 * the advisor doesn't use the desktop canvas).
 *
 * Same shape as GET /api/listings/{ulid}/deal-comparables plus
 * `type: "deal_comparables"`. All navigation is internal via
 * react-router (external source URL is a small secondary action).
 */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Check,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Info,
  MapPin,
} from "lucide-react";
import { saveAdvisorReturnState } from "../../../utils/advisorReturnStore";
import { captureAdvisorSnapshot } from "../../../utils/advisorScrollSnapshot";

function fmtDistance(m, t) {
  if (m == null) return null;
  if (t) {
    if (m < 1000)
      return t("advisor.comparables.distanceM", { m: Math.round(m) });
    return t("advisor.comparables.distanceKm", {
      km: (m / 1000).toFixed(1).replace(".", ","),
    });
  }
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(1).replace(".", ",")} km`;
}

function CompRow({ item, kind, onOpen, t }) {
  // P0.4c Punkt 4 — defensive ULID resolution.
  const resolvedId = item.ulid || item.id;
  const isActive = item.is_active !== false && item.status === "active";
  const priceLabel =
    kind === "rental"
      ? item.rent_display ||
        (item.rent_monthly != null
          ? t("advisor.comparables.rentPerMonth", {
              value: item.rent_monthly.toLocaleString("de-AT"),
            })
          : "—")
      : item.price_display ||
        (item.price != null
          ? `${item.price.toLocaleString("de-AT")} €`
          : "—");
  const suffix = kind === "rental" ? t("advisor.comparables.perMonth", { defaultValue: "" }) : "";
  const dist = fmtDistance(item.distance_m, t);
  const openExternal = (e) => {
    e.stopPropagation();
    if (item.url) window.open(item.url, "_blank", "noopener,noreferrer");
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => resolvedId && onOpen(resolvedId)}
      onKeyDown={(e) => e.key === "Enter" && resolvedId && onOpen(resolvedId)}
      data-testid={`advisor-comp-row-${resolvedId}`}
      className={`flex gap-2.5 rounded-lg border p-2 hover:border-emerald-500/50 transition-colors cursor-pointer ${
        isActive
          ? "border-zinc-800 bg-zinc-900/60"
          : "border-zinc-800/70 bg-zinc-950/40 opacity-70"
      }`}
    >
      {item.image ? (
        <img
          src={item.image}
          alt=""
          className="h-12 w-16 flex-shrink-0 rounded object-cover"
          loading="lazy"
        />
      ) : (
        <div className="h-12 w-16 flex-shrink-0 rounded bg-zinc-800" />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="text-xs font-medium text-zinc-200 line-clamp-1 flex-1">
            {item.title || "—"}
          </div>
          {item.url ? (
            <button
              type="button"
              onClick={openExternal}
              title={t("advisor.comparables.openOriginal")}
              aria-label={t("advisor.comparables.openOriginal")}
              data-testid={`advisor-comp-external-${resolvedId}`}
              className="shrink-0 p-1 -m-1 rounded hover:bg-zinc-800/60"
            >
              <ExternalLink className="h-3 w-3 text-zinc-600 hover:text-emerald-300" />
            </button>
          ) : null}
        </div>
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <div className="text-xs font-semibold text-emerald-300">
            {priceLabel}
            {suffix && (
              <span className="text-[10px] font-normal text-zinc-500">
                {" "}
                {suffix}
              </span>
            )}
          </div>
          {dist && (
            <span className="inline-flex items-center gap-0.5 text-[10px] text-zinc-500">
              <MapPin className="h-2.5 w-2.5" />
              {dist}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-x-2 gap-y-0 text-[10px] text-zinc-500 mt-0.5">
          {item.living_area_m2 != null && (
            <span>
              {t("advisor.comparables.areaM2", {
                value: Math.round(item.living_area_m2),
              })}
            </span>
          )}
          {item.rooms != null && (
            <span>
              {t("advisor.comparables.roomsShort", { count: item.rooms })}
            </span>
          )}
          {kind === "rental" && item.rent_per_m2 != null && (
            <span>
              {t("advisor.comparables.eurPerM2", {
                value: item.rent_per_m2,
              })}
            </span>
          )}
          {kind !== "rental" && item.price_per_m2 != null && (
            <span>
              {t("advisor.comparables.eurPerM2", {
                value: item.price_per_m2,
              })}
            </span>
          )}
          {item.postal_code && (
            <span>
              {t("advisor.comparables.postalCode", {
                code: item.postal_code,
              })}
            </span>
          )}
        </div>
        {!isActive && (
          <div className="text-[10px] text-amber-400/80 mt-0.5">
            {t("advisor.comparables.inactive")}
          </div>
        )}
      </div>
    </div>
  );
}

export function DealComparablesChatWidget({ widget }) {
  const { t, i18n } = useTranslation("ai");
  const navigate = useNavigate();
  const [showExcluded, setShowExcluded] = useState(false);
  if (!widget) return null;
  const lang = (widget.language || i18n.language || "de")
    .slice(0, 2)
    .toLowerCase();
  const purchase = widget.purchase_comparables || {};
  const rental = widget.rental_comparables || null;
  const excluded = widget.excluded_comparables || [];
  const notice = widget.notice_i18n?.[lang] || widget.notice_i18n?.de || "";

  const openInternal = (id) => {
    if (!id) return;
    // P0.4c Punkt 3 + P0.4d Punkt 1 — snapshot advisor state so the
    // source listing re-opens the panel + loads the same conversation
    // AND restores the EXACT chat scroll offset on BACK. The
    // snapshotter is registered by AIAdvisorPanel and reads the live
    // container `scrollTop` + the topmost visible turn's `turn_ulid`.
    const snap = captureAdvisorSnapshot(widget.listing_ulid);
    saveAdvisorReturnState(widget.listing_ulid, {
      conversationId: widget.conversation_id || null,
      wasOpen: true,
      chatScrollTop: snap?.chatScrollTop,
      anchorTurnUlid: snap?.anchorTurnUlid ?? null,
      anchorTurnIndex: snap?.anchorTurnIndex ?? null,
    });
    navigate(`/listing/${id}`);
  };

  const purchaseItems = purchase.items || [];
  const rentalItems = rental?.items || [];

  return (
    <div
      data-testid="advisor-deal-comparables-widget"
      className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-3 space-y-3"
    >
      {notice && (
        <div
          data-testid="advisor-deal-comparables-notice"
          className="flex items-start gap-1.5 text-[11px] text-zinc-400"
        >
          <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
          <span>{notice}</span>
        </div>
      )}

      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-zinc-300 mb-1.5">
          {t("advisor.comparables.purchaseHeading")}
          <span className="ml-2 text-[10px] font-normal normal-case text-zinc-500">
            {purchaseItems.length > 0
              ? t("advisor.comparables.activeCount", {
                  count: purchaseItems.length,
                })
              : t("advisor.comparables.emptyPurchase")}
          </span>
        </div>
        {purchaseItems.length > 0 ? (
          <div className="space-y-1.5">
            {purchaseItems.slice(0, 8).map((it) => (
              <CompRow
                key={it.ulid || it.id}
                item={it}
                kind="purchase"
                onOpen={openInternal}
                t={t}
              />
            ))}
          </div>
        ) : (
          <p
            data-testid="advisor-deal-comparables-purchase-empty"
            className="text-[11px] text-zinc-500"
          >
            {t("advisor.comparables.emptyPurchaseHint")}
          </p>
        )}
      </div>

      {rental && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-zinc-300 mb-1.5">
            {t("advisor.comparables.rentalHeading")}
            <span className="ml-2 text-[10px] font-normal normal-case text-zinc-500">
              {rentalItems.length > 0
                ? t("advisor.comparables.activeCount", {
                    count: rentalItems.length,
                  })
                : t("advisor.comparables.emptyRental")}
            </span>
          </div>
          {rentalItems.length > 0 ? (
            <div className="space-y-1.5">
              {rentalItems.slice(0, 8).map((it) => (
                <CompRow
                  key={it.ulid || it.id}
                  item={it}
                  kind="rental"
                  onOpen={openInternal}
                  t={t}
                />
              ))}
            </div>
          ) : (
            <p
              data-testid="advisor-deal-comparables-rental-empty"
              className="text-[11px] text-zinc-500"
            >
              {t("advisor.comparables.emptyRentalHint")}
            </p>
          )}
        </div>
      )}

      {excluded.length > 0 && (
        <div>
          <button
            type="button"
            data-testid="advisor-deal-comparables-excluded-toggle"
            onClick={() => setShowExcluded((v) => !v)}
            className="flex items-center gap-1 w-full justify-between px-2.5 py-1.5 rounded-lg border border-zinc-800 bg-zinc-900/60 hover:border-red-500/40 text-[11px] text-zinc-400"
          >
            <span>
              {showExcluded
                ? t("advisor.comparables.hideExcluded")
                : t("advisor.comparables.showExcluded", {
                    count: excluded.length,
                  })}
            </span>
            {showExcluded ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
          </button>
          {showExcluded && (
            <div className="mt-2 space-y-1.5">
              {excluded.map((x, idx) => (
                <div
                  key={`${x.id}-${idx}`}
                  data-testid={`advisor-deal-comp-excluded-${x.id}`}
                  className="rounded-lg border border-red-900/50 bg-red-950/25 px-2.5 py-1.5 text-[11px]"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-red-200 font-medium">
                      {t(`advisor.comparables.reasons.${x.reason}`, {
                        defaultValue: x.reason_label || x.reason,
                      })}
                    </span>
                    {x.rent_monthly != null && (
                      <span className="text-red-300 tabular-nums">
                        {t("advisor.comparables.rentPerMonthShort", {
                          value: x.rent_monthly.toLocaleString("de-AT"),
                        })}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-2 text-[10px] text-zinc-500 mt-0.5">
                    {x.rent_per_m2 != null && (
                      <span>
                        {t("advisor.comparables.eurPerM2", {
                          value: x.rent_per_m2,
                        })}
                      </span>
                    )}
                    {x.living_area_m2 != null && (
                      <span>
                        {t("advisor.comparables.areaM2", {
                          value: Math.round(x.living_area_m2),
                        })}
                      </span>
                    )}
                    {x.postal_code && (
                      <span>
                        {t("advisor.comparables.postalCode", {
                          code: x.postal_code,
                        })}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
